import { useContext, useEffect, useReducer, useState } from "react";

import { ApiHelper } from "@datanac/datanac-api-toolkit/dist/api-helper";
import { getCurrentReinsuranceYear } from "@datanac/datanac-api-toolkit/dist/utility/InsurancePlanHelper";
import { _sessionManager } from "api/ApiHelper";
import { AppContext } from "App";
import { BUDGET_STATUS_ACTIVE } from "components/Budget/helpers";
import EmptyCard from "components/Cards/EmptyCard";
import { propUpdateReducer } from "components/Core/propUpdateReducer";
import { useFarmController } from "components/Vault/Farm/useFarmController";
import WorkspaceHeader from "components/Workspaces/WorkspaceHeader";
import { sumBy } from "lodash";
import { useSnackbar } from "notistack";
import { formatPrice } from "utility/formatters";

import { ReportsIcon } from "../../Icons/Icons";
import { sortAmount } from "./helpers";
import { ReportViewer } from "./ReportViewer";

export default function BudgetSummaryReport() {
  const { globalState, globalDispatch } = useContext(AppContext);

  const [currentYear, setCurrentYear] = useState(
    globalState?.crop_year ? globalState?.crop_year : getCurrentReinsuranceYear()
  );
  useEffect(() => {
    globalDispatch({
      type: "update",
      payload: {
        key: "crop_year",
        value: currentYear,
      },
    });
    budgetSummaryReport();
  }, [currentYear]);

  const [filter, dispatchFilter] = useReducer(propUpdateReducer, {
    commodity_name: [],
    practice_name: [],
  });

  const { enqueueSnackbar } = useSnackbar();
  const handleError = (err) => {
    console.warn(err);
    enqueueSnackbar(err.detail || err.message || err, { preventDuplicate: true, variant: "error" });
  };

  const [budgetSummaryReportData, setBudgetSummaryReportData] = useState([]);
  const [filteredBudgetSummaryReportData, setFilteredBudgetSummaryReportData] = useState([]);

  const { isLoadingCounties } = useFarmController({
    crop_year: currentYear,
    filter: filter,
    onError: handleError,
  });

  const budgetSummaryReport = async () => {
    try {
      if (globalState?.producer_token) {
        const _filter = {
          producer_token: globalState?.producer_token,
          is_active: true,
          year: currentYear,
          status: BUDGET_STATUS_ACTIVE,
        };

        const api = new ApiHelper({
          baseUrl: import.meta.env.VITE_DATANAC_USERS_API_URL_BASE,
          config: {},
          sessionManager: _sessionManager,
          // No caching:
          _instance: null,
        });
        const _dataDictionary = await api.callPost(
          "data/Users/Budget/BudgetSummaryReport/ExportObjectGraph",
          _filter,
          globalState
        );
        setBudgetSummaryReportData(transformBudgetData(_dataDictionary));
        setFilteredBudgetSummaryReportData(transformBudgetData(_dataDictionary));
      }
    } catch (err) {
      handleError(err);
    }
  };

  // Filter the data based on the filter state
  useEffect(() => {
    const budgetSummaryData = budgetSummaryReportData?.filter((c) => {
      const commodityFilter = !filter?.commodity_name?.length || filter?.commodity_name?.includes(c.commodity_name);
      const practiceFilter = !filter?.practice_name?.length || filter?.practice_name?.includes(c.practice_name);
      return commodityFilter && practiceFilter;
    });

    setFilteredBudgetSummaryReportData(budgetSummaryData);
  }, [budgetSummaryReportData, filter]);

  const columnsDirective = [
    { field: "name", headerText: "Scenario", width: 150 },
    { field: "location_county_name", headerText: "County", width: 100 },
    { field: "location_state_name", headerText: "State", width: 100 },
    { field: "commodity_name", headerText: "Commodity", width: 150 },
    { field: "type_name", headerText: "Type", width: 100 },
    { field: "practice_name", headerText: "Practice", width: 100 },
    { field: "reported_acreage", headerText: "Reported Acreage", width: 100, textAlign: "Right" },
    { field: "yield", headerText: "Approved Yield", width: 100, textAlign: "Right" },
    { field: "projected_price", headerText: "Price", width: 100, textAlign: "Right" },
    {
      field: "revenue_per_acre",
      headerText: "Revenue / Acre",
      width: 100,
      textAlign: "Right",
      sortComparer: sortAmount,
    },
    { field: "cost_per_acre", headerText: "Cost / Acre", width: 100, textAlign: "Right", sortComparer: sortAmount },
    {
      field: "net_income_per_acre",
      headerText: "Net Income / Acre",
      width: 100,
      textAlign: "Right",
      sortComparer: sortAmount,
    },
    { field: "revenue", headerText: "Revenue", width: 100, textAlign: "Right", sortComparer: sortAmount },
    { field: "cost", headerText: "Cost", width: 100, textAlign: "Right", sortComparer: sortAmount },
    { field: "net_income", headerText: "Net Income", width: 100, textAlign: "Right", sortComparer: sortAmount },
  ];

  const calculateProductionCost = (plan) => {
    return sumBy(plan.BudgetCountyPracticeLineItem, (i) => Number(i.value) || 0);
  };

  const transformBudgetData = (originalData) => {
    return originalData?.flatMap((item) =>
      item.budgetcountypractice?.map((plan) => ({
        ...item,
        location_state_name: plan.state_name,
        location_county_name: plan.county_name,
        commodity_name: plan.commodity_name,
        type_name: plan.type_name,
        practice_name: plan.practice_name,
        reported_acreage: plan.reported_acreage,
        yield: plan.yield,
        projected_price: plan.projected_price,
        revenue_per_acre: formatPrice(plan?.yield * plan?.projected_price, 0),
        cost_per_acre: formatPrice(calculateProductionCost(plan), 0),
        net_income_per_acre: formatPrice(plan?.yield * plan?.projected_price - calculateProductionCost(plan), 0),
        revenue: formatPrice(plan?.yield * plan?.projected_price * plan?.reported_acreage, 0),
        cost: formatPrice(calculateProductionCost(plan) * plan?.reported_acreage, 0),
        net_income: formatPrice(
          (plan?.yield * plan?.projected_price - calculateProductionCost(plan)) * plan?.reported_acreage,
          0
        ),
      }))
    );
  };

  return (
    <>
      <WorkspaceHeader
        title="Budget Summary"
        category={{
          title: "Reports",
          icon: <ReportsIcon />,
        }}
      />
      <ReportViewer
        columnsDirective={columnsDirective}
        counties={filteredBudgetSummaryReportData}
        producerFarmFieldCrops={filteredBudgetSummaryReportData}
        isLoadingCounties={isLoadingCounties}
        reportType="budget"
        currentYear={currentYear}
        setCurrentYear={setCurrentYear}
        gridKey="BudgetSummaryReport"
        filter={filter}
        dispatchFilter={dispatchFilter}
      />

      {!isLoadingCounties && !filteredBudgetSummaryReportData?.length && (
        <EmptyCard text="No search results" secondaryText="Remove some filters or add a new farm in the Farm Vault" />
      )}
    </>
  );
}
